import React, { useEffect, useState } from "react";
import { Tag, Tooltip, Button, Modal, InputNumber, Space } from "antd";
import { EditOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";
import { MemberProfile } from "../../../../model/members/MemberProfile";
import { tokens } from "../../../../core/theme/tokens";
import { useFormatCurrency, useCurrencySymbol } from "../../../../hooks/useFormatters";

interface DirectDebitOverrideMarkerProps {
    member: MemberProfile;
    // Admin + Front-of-House may edit/add/delete; everyone else sees the marker
    // read-only (self-service included).
    canEdit: boolean;
    // Persists the new override amount, or null to clear it. Returns a promise so
    // the modal can show a saving state and close on success.
    onSave: (amount: number | null) => Promise<void> | void;
}

// Reads the raw DirectDebitOverrideAmount metadata (a number or numeric string)
// into a number, or null when absent / unparseable.
const readOverride = (member: MemberProfile): number | null => {
    const raw = member.metadata?.DirectDebitOverrideAmount;
    if (raw === undefined || raw === null || raw === "") return null;
    const value = Number(raw);
    return Number.isNaN(value) ? null : value;
};

// Amber "Price override" marker shown in the membership-category area whenever a
// member has a custom direct-debit price. Visible to everyone (including the
// member on their own profile) and always shows the amount; Admin/Front-of-House
// additionally get an Edit (or Add Override) button opening a validated modal.
const DirectDebitOverrideMarker: React.FC<DirectDebitOverrideMarkerProps> = ({ member, canEdit, onSave }) => {
    const formatCurrency = useFormatCurrency();
    const currencySymbol = useCurrencySymbol();

    const override = readOverride(member);
    const hasOverride = override !== null;

    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState<number | null>(override);
    const [saving, setSaving] = useState(false);

    // Re-seed the modal's amount whenever the stored override changes or the modal
    // is (re)opened, so a stale edit is never carried over.
    useEffect(() => {
        if (open) setAmount(override);
    }, [open, override]);

    const openModal = () => setOpen(true);
    const closeModal = () => {
        if (!saving) setOpen(false);
    };

    const valid = amount !== null && !Number.isNaN(amount) && amount >= 0;

    const persist = async (value: number | null) => {
        setSaving(true);
        try {
            await onSave(value);
            setOpen(false);
        } finally {
            setSaving(false);
        }
    };

    // Nothing to show: no override and the viewer can't add one.
    if (!hasOverride && !canEdit) return null;

    return (
        <>
            {hasOverride && (
                <Tooltip title="This member has a custom direct-debit price that replaces their membership category's amount.">
                    <Tag
                        icon={<WarningOutlined />}
                        color={tokens.color.warning}
                        data-testid="dd-override-tag"
                        style={{ marginInlineEnd: 0, fontWeight: tokens.fontWeight.semibold }}
                    >
                        Price override: {formatCurrency(override)}
                    </Tag>
                </Tooltip>
            )}

            {canEdit && hasOverride && (
                <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={openModal}
                    data-testid="dd-override-edit-btn"
                    style={{ paddingInline: tokens.space[1] }}
                >
                    Edit
                </Button>
            )}

            {canEdit && !hasOverride && (
                <Button
                    type="link"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={openModal}
                    data-testid="dd-override-add-btn"
                    style={{ paddingInline: tokens.space[1] }}
                >
                    Add override
                </Button>
            )}

            <Modal
                title={hasOverride ? "Edit direct-debit override" : "Add direct-debit override"}
                open={open}
                onCancel={closeModal}
                destroyOnClose
                footer={[
                    hasOverride ? (
                        <Button
                            key="delete"
                            danger
                            loading={saving}
                            onClick={() => persist(null)}
                            data-testid="dd-override-delete-btn"
                        >
                            Delete override
                        </Button>
                    ) : null,
                    <Button key="cancel" onClick={closeModal} disabled={saving}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        loading={saving}
                        disabled={!valid}
                        onClick={() => persist(amount)}
                        data-testid="dd-override-save-btn"
                    >
                        Save
                    </Button>,
                ]}
            >
                <Space direction="vertical" style={{ width: "100%" }} size={tokens.space[2]}>
                    <span style={{ color: tokens.color.text.secondary }}>
                        This amount replaces the member's membership-category price on every direct-debit run.
                    </span>
                    <InputNumber
                        inputMode="decimal"
                        value={amount}
                        onChange={setAmount}
                        min={0}
                        step={0.01}
                        precision={2}
                        prefix={currencySymbol}
                        placeholder="Override amount"
                        data-testid="dd-override-amount-input"
                        style={{ width: "100%" }}
                    />
                </Space>
            </Modal>
        </>
    );
};

export default DirectDebitOverrideMarker;
